import {
  CustomReward,
  CustomRewardCurrency,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Typography,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"

import RewardRedeemButton from "./RewardRedeemButton"
import SettingsGearButton from "../../../components/buttons/SettingsGearButton"
import { useFamilyQuery } from "../../../networking/queries"
import {
  useCustomRewardBalancesQuery,
  useScreenTimeBalancesQuery,
} from "../../../networking/rewards/queries"
import { FamilyQuerySelect } from "../../../types"
import { ANALYTIC_EVENTS } from "../../../util/analytics"
import { trackAnalyticEvent } from "../../../util/analytics"
import { getScreenTimeConfigurations } from "../../../util/util"
import useAutoApproveModalStore from "../hooks/useAutoApproveModalStore"

type Props = {
  childId: number
  reward: CustomReward
  isGrouped?: boolean
  style?: React.CSSProperties
}

const RewardChildRedeemCard = ({
  childId,
  reward,
  isGrouped,
  style,
}: Props) => {
  const { t } = useTranslation()
  const { onOpen } = useAutoApproveModalStore()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const { data: rewardsBalances } = useCustomRewardBalancesQuery({
    rewardId: reward.id,
  })
  const { data: screenTimeBalances } = useScreenTimeBalancesQuery({
    rewardId: reward.id,
  })
  const { numLinkedDevices } = getScreenTimeConfigurations(reward, childId)
  const rewardBalance = rewardsBalances?.find(
    (balance) => balance.user_id === childId
  )
  const rewardBalanceReadyToUse = screenTimeBalances?.find(
    (balance) => balance.user_id === childId
  )
  const balance = rewardBalance?.balance || 0

  const balanceText =
    reward.currency === CustomRewardCurrency.DOLLAR
      ? `$${balance}`
      : reward.currency === CustomRewardCurrency.MINUTE
        ? balance === 1
          ? t("REWARD_MINS_ONE", { count: balance })
          : t("REWARD_MINS_OTHER", { count: balance })
        : `${balance}`

  const child = children?.find((child) => child.user.id === childId)

  const shouldShowAutoApproveSettingsButton =
    reward.currency === CustomRewardCurrency.MINUTE && numLinkedDevices > 0

  const isAutoApprovable = reward.assignments?.find(
    (assignment) => assignment.user_id === childId
  )?.bypass_redeem

  const hasLinkedDevices = numLinkedDevices > 0

  const onClickAutoApproveSettings = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_AUTO_REDEEM_SETTINGS)
    onOpen(reward.id, childId)
  }

  return (
    <div
      className="flex flex-col w-full border border-borderPrimary rounded-md bg-backgroundPrimary"
      style={style}
    >
      <div
        className={`flex flex-row justify-between gap-2 items-center w-full px-3 ${
          !isGrouped && hasLinkedDevices ? "pt-2" : "py-3"
        }`}
      >
        {isGrouped ? (
          <div className="flex flex-row justify-start gap-1 items-center w-[60%]">
            <Typography
              variant="bodySmallBold"
              style={{
                backgroundColor:
                  balance > 0
                    ? JoonColorExpanded.green200
                    : JoonUIColor.icon.light,
                padding: `${SPACING.space05} ${SPACING.space1}`,
                borderRadius: SPACING.space1,
                whiteSpace: "nowrap",
              }}
            >
              {balanceText}
            </Typography>
            <Typography
              variant="bodySmallBold"
              textAlign="left"
              style={{
                textOverflow: "ellipsis",
                width: "100%",
                overflow: "hidden",
                whiteSpace: "nowrap",
              }}
            >
              {child?.user.name}
            </Typography>
          </div>
        ) : (
          <Typography
            variant="bodySmallBold"
            textAlign="left"
            style={{
              textOverflow: "ellipsis",
              maxWidth: "60%",
              overflow: "hidden",
            }}
          >
            {child?.user.name}
          </Typography>
        )}
        <div className="flex flex-row justify-end gap-2 items-center w-fit h-full">
          {rewardBalance?.balance ? (
            <RewardRedeemButton rewardBalance={rewardBalance} />
          ) : (
            !rewardBalanceReadyToUse?.balance && (
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.secondary}
                style={{
                  textAlign: "right",
                  fontStyle: "italic",
                  width: "fit-content",
                }}
              >
                {reward.currency === CustomRewardCurrency.MINUTE
                  ? t("REWARD_NOTHING_TO_APPROVE")
                  : t("REWARD_NOTHING_TO_USE")}
              </Typography>
            )
          )}
          {shouldShowAutoApproveSettingsButton && (
            <SettingsGearButton onClick={onClickAutoApproveSettings} />
          )}
        </div>
      </div>
      {!isGrouped && hasLinkedDevices && (
        <Typography
          variant="bodySmall"
          color={JoonUIColor.text.secondary}
          className="pb-2 px-3"
        >
          {numLinkedDevices === 1
            ? t("REWARD_ST_LINKED_ONE", { count: numLinkedDevices })
            : t("REWARD_ST_LINKED_OTHER", { count: numLinkedDevices })}
        </Typography>
      )}
      {!isGrouped && (
        <div className="flex flex-row justify-between gap-2 items-center w-full border-t border-borderPrimary py-2 px-3">
          <Typography
            variant="bodySmallBold"
            style={{
              backgroundColor:
                balance > 0
                  ? JoonColorExpanded.green200
                  : JoonUIColor.icon.light,
              padding: `${SPACING.space05} ${SPACING.space1}`,
              borderRadius: SPACING.space1,
            }}
          >
            {balanceText}
            <Typography variant="bodySmall">
              {" "}
              {t("REWARD_PURCHASED_LABEL")}
            </Typography>
            {rewardBalanceReadyToUse?.balance && (
              <>
                <Typography variant="bodySmall">{` | `}</Typography>
                {rewardBalanceReadyToUse.balance === 1
                  ? t("REWARD_MINS_ONE", {
                      count: rewardBalanceReadyToUse.balance,
                    })
                  : t("REWARD_MINS_OTHER", {
                      count: rewardBalanceReadyToUse.balance,
                    })}{" "}
                <Typography variant="bodySmall">
                  {t("REWARD_READY_TO_USE")}
                </Typography>
              </>
            )}
          </Typography>
          {isAutoApprovable && (
            <Typography
              variant="bodySmall"
              textAlign="right"
              color={JoonUIColor.text.primaryAccent}
              style={{ width: "fit-content" }}
            >
              {t("REWARD_AUTO_APPROVABLE")}
            </Typography>
          )}
        </div>
      )}
    </div>
  )
}

export default RewardChildRedeemCard
